import React, { ChangeEvent, useRef, useState } from "react";
import { useMemoizedFn } from "ahooks";
import { IonIcon, IonSpinner, useIonAlert } from "@ionic/react";
import { attach, send } from "ionicons/icons";
import { useTranslation } from "react-i18next";

type ChatComposerProps = {
  isClosed: boolean;
  isSendingMessage: boolean;
  isSendingFile: boolean;
  onSendText: (text: string) => void;
  onSendFile: (file: File) => void;
};

const FILE_ACCEPT = "image/*,video/mp4,application/pdf";

const ChatComposer = ({ isClosed, isSendingMessage, isSendingFile, onSendText, onSendFile }: ChatComposerProps) => {
  const { t } = useTranslation();
  const [presentAlert] = useIonAlert();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState("");

  const trimmed = text.trim();
  const canSend = !isClosed && !isSendingMessage && trimmed.length > 0;

  const handleSend = useMemoizedFn(() => {
    if (!canSend) return;
    onSendText(trimmed);
    setText("");
    messageInputRef.current?.focus();
  });

  const handlePickFile = useMemoizedFn(() => {
    fileInputRef.current?.click();
  });

  const handleFileChange = useMemoizedFn((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isClosed) return;

    presentAlert({
      header: t("Send media file?"),
      message: t("Confirm media file send", { fileName: file.name }),
      buttons: [
        {
          text: t("Cancel"),
          role: "cancel",
        },
        {
          text: t("Send"),
          role: "confirm",
          handler: () => onSendFile(file),
        },
      ],
    });
  });

  const placeholder = isClosed ? (t("Chat is closed") as string) : (t("Write a message") as string);

  return (
    <div className="flex items-center gap-2 p-2 border-t border-gray-200 bg-white">
      <button
        type="button"
        onClick={handlePickFile}
        disabled={isClosed || isSendingFile}
        className="p-2 text-gray-500 disabled:opacity-50"
      >
        {isSendingFile ? <IonSpinner name="dots" /> : <IonIcon icon={attach} className="text-2xl" />}
      </button>

      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} accept={FILE_ACCEPT} />

      <textarea
        ref={messageInputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={1}
        disabled={isClosed}
        className="flex-1 resize-none border border-gray-200 rounded-2xl px-3 py-2 text-sm focus:outline-none disabled:bg-gray-100"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
      />
      <button type="button" onClick={handleSend} disabled={!canSend} className="p-2 text-primary disabled:opacity-40">
        <IonIcon icon={send} className="text-2xl" />
      </button>
    </div>
  );
};

export default ChatComposer;
